"use client";

import type { ReactNode } from "react";
import { cn } from "@resume-studio/utils";
import { AlertCircle, CheckCircleIcon, Clock, Loader2, Sparkles } from "lucide-react";

export type ToolCallCardStatus =
  | "pending"
  | "approval"
  | "running"
  | "success"
  | "error"
  | "denied"
  | "undone";

export type ToolActionType = "add" | "update" | "delete";

export interface ToolCallCardProps {
  icon?: ReactNode;
  title?: string;
  status: ToolCallCardStatus;
  /** The type of action: add (default), update, or delete. Affects copy and styling. */
  actionType?: ToolActionType;
  summary?: ReactNode;
  actions?: ReactNode;
  className?: string;
  summaryClassName?: string;
}

const STATUS_STYLES: Record<
  ToolCallCardStatus,
  {
    container: string;
    badge: string;
    label: string;
    defaultSummary: string;
    helper?: string;
    indicator: ReactNode;
  }
> = {
  pending: {
    container: "border-slate-200/70 bg-white/90",
    badge: "bg-slate-100 text-slate-600",
    label: "Waiting",
    defaultSummary: "We’ll begin in just a moment.",
    helper: "We’re getting things ready.",
    indicator: <Clock className="size-4 text-slate-500" />,
  },
  approval: {
    container: "border-sky-200 bg-sky-50/60",
    badge: "bg-sky-100 text-sky-700",
    label: "Ready to add",
    defaultSummary: "If this looks right, I’ll add it to your resume.",
    helper: "I drafted this from your last message.",
    indicator: <AlertCircle className="size-4 text-sky-600" />,
  },
  denied: {
    container: "border-slate-200/70 bg-white/90",
    badge: "bg-slate-100 text-slate-600",
    label: "Dismissed",
    defaultSummary: "Suggestion dismissed.",
    helper: "No changes were made.",
    indicator: <Clock className="size-4 text-slate-500" />,
  },
  undone: {
    container: "border-slate-200/70 bg-white/90",
    badge: "bg-slate-100 text-slate-600",
    label: "Undone",
    defaultSummary: "Change undone.",
    helper: "The update was reverted.",
    indicator: <Clock className="size-4 text-slate-500" />,
  },
  running: {
    container: "border-sky-200/80 bg-sky-50/70",
    badge: "bg-sky-100 text-sky-700",
    label: "Working",
    defaultSummary: "Hang tight—we’re working on this for you.",
    helper: "Applying your update.",
    indicator: <Loader2 className="size-4 animate-spin text-sky-600" />,
  },
  success: {
    container: "border-emerald-200/80 bg-emerald-50/70",
    badge: "bg-emerald-100 text-emerald-700",
    label: "Applied",
    defaultSummary: "Done! Your changes are saved.",
    helper: "All set.",
    indicator: <CheckCircleIcon className="size-4 text-emerald-600" />,
  },
  error: {
    container: "border-rose-200/80 bg-rose-50/70",
    badge: "bg-rose-100 text-rose-700",
    label: "Needs attention",
    defaultSummary: "Something went wrong—please try again.",
    helper: "We couldn’t complete this.",
    indicator: <AlertCircle className="size-4 text-rose-600" />,
  },
};

/** Action-specific overrides for approval state */
const ACTION_APPROVAL_STYLES: Record<
  ToolActionType,
  {
    container: string;
    badge: string;
    label: string;
    proposalLabel: string;
    trustNote: string | null;
    indicator: ReactNode;
    labelColor: string;
  }
> = {
  add: {
    container: "border-sky-200 bg-sky-50/60",
    badge: "bg-sky-100 text-sky-700",
    label: "Ready to add",
    proposalLabel: "Here's what I can add",
    trustNote: "You can edit this anytime after it's added.",
    indicator: <AlertCircle className="size-4 text-sky-600" />,
    labelColor: "text-sky-700",
  },
  update: {
    container: "border-sky-200 bg-sky-50/60",
    badge: "bg-sky-100 text-sky-700",
    label: "Ready to update",
    proposalLabel: "Here's the change",
    trustNote: "You can edit this anytime after it's applied.",
    indicator: <AlertCircle className="size-4 text-sky-600" />,
    labelColor: "text-sky-700",
  },
  delete: {
    container: "border-amber-200 bg-amber-50/60",
    badge: "bg-amber-100 text-amber-700",
    label: "Ready to remove",
    proposalLabel: "Ready to remove",
    trustNote: null, // No reassurance needed for deletes
    indicator: <AlertCircle className="size-4 text-amber-600" />,
    labelColor: "text-amber-700",
  },
};

export const ToolCallCard = ({
  icon,
  title,
  status,
  actionType = "add",
  summary,
  actions,
  className,
  summaryClassName,
}: ToolCallCardProps) => {
  const baseMeta = STATUS_STYLES[status];
  const isApproval = status === "approval";
  const isSuccess = status === "success";
  const isCompletedState = isSuccess || status === "denied" || status === "undone";

  // For approval state, use action-specific overrides
  const actionMeta = isApproval ? ACTION_APPROVAL_STYLES[actionType] : null;

  const containerClass = actionMeta?.container ?? baseMeta.container;
  const badgeClass = actionMeta?.badge ?? baseMeta.badge;
  const label = actionMeta?.label ?? baseMeta.label;
  const indicator = actionMeta?.indicator ?? baseMeta.indicator;
  const proposalLabel = actionMeta?.proposalLabel ?? null;
  const trustNote = actionMeta?.trustNote ?? null;
  const labelColor = actionMeta?.labelColor ?? "text-sky-700";

  const displaySummary = summary || baseMeta.defaultSummary;
  const summaryTone = summary ? "text-sm text-foreground" : "text-xs text-muted-foreground";

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2 rounded-lg border p-3 shadow-sm transition-all",
        containerClass,
        className,
      )}
      role="status"
      aria-live={status === "running" ? "polite" : "off"}
    >
      {/* Row 1: Title + Status (hide badge for completed states - shown at bottom) */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm">
          {icon ?? <Sparkles className="size-3" />}
        </div>
        {title && <span className="text-sm font-medium text-foreground">{title}</span>}
        {!isCompletedState && (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {indicator}
            <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", badgeClass)}>
              {label}
            </span>
          </div>
        )}
      </div>

      {/* Row 2: Proposal label (only for approval) */}
      {isApproval && proposalLabel && (
        <p className={cn("text-[10px] font-semibold uppercase tracking-wide", labelColor)}>
          {proposalLabel}
        </p>
      )}

      {/* Row 3: Content box */}
      <div
        className={cn(
          "break-words rounded-lg border border-white/40 bg-white/70 px-2.5 py-2 [&_a]:text-foreground [&_a]:underline [&_a]:decoration-dotted",
          summaryTone,
          summaryClassName,
        )}
      >
        {displaySummary}
      </div>

      {/* Row 4: Status label at bottom for completed states (success/denied/undone) */}
      {isCompletedState && (
        <div className="flex items-center gap-1.5">
          {indicator}
          <span className={cn("text-xs font-medium", badgeClass.replace(/bg-\S+/, "").trim())}>
            {label}
          </span>
        </div>
      )}

      {/* Row 5: Trust note (only for approval) */}
      {isApproval && trustNote && <p className="text-[10px] text-muted-foreground">{trustNote}</p>}

      {/* Row 6: Actions */}
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
};
